import CheckIcon from '@mui/icons-material/Check';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';
import { useRecoilState } from 'recoil';

import { IFilters } from 'Interfaces/general';
import { collections, sort } from 'lib/constants';
import { marketplaceFiltersState } from 'lib/recoil/atoms';

const menu: Record<IFilters, Array<{ key: string; name: string }>> = {
	collection: collections.map(collection => ({
		key: collection.key,
		name: collection.name
	})),
	sort: sort.map(sort => ({
		key: sort.key,
		name: sort.name
	}))
};

export const MarketplaceFilters = () => {
	const [marketplaceFilters, setMarketplaceFilters] = useRecoilState(marketplaceFiltersState);
	const [filterMenuType, setFiltersType] = useState<IFilters | null>(null);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleOnFilterClick = (e: React.MouseEvent<HTMLElement>, filterMenuType: IFilters) => {
		setAnchorEl(e.currentTarget);
		setFiltersType(filterMenuType);
	};

	const handleOnFilterDelete = (key: IFilters) => {
		setMarketplaceFilters(prevState => ({
			...prevState,
			[key]: ''
		}));

		setFiltersType(null);
	};

	const handleOnFiltersClose = () => {
		setAnchorEl(null);

		setFiltersType(null);
	};

	const handleMenuItemClick = (key: string) => {
		if (!filterMenuType) {
			return;
		}

		setMarketplaceFilters(prevState => ({
			...prevState,
			pagination: 1,
			[filterMenuType]: key
		}));

		setAnchorEl(null);

		setFiltersType(null);
	};

	const findFilterLabel = (key: IFilters) => {
		if (key === 'collection') {
			return marketplaceFilters.collection
				? collections.find(collection => collection.key === marketplaceFilters.collection)?.name ?? 'Collection'
				: 'Collection';
		} else {
			return marketplaceFilters.sort
				? sort.find(sort => sort.key === marketplaceFilters.sort)?.name ?? 'Sort'
				: 'Sort';
		}
	};

	const handleOnResetFilter = () => {
		// Reset filters and go back to first page
		setMarketplaceFilters({
			pagination: 1,
			collection: null,
			sort: null
		});
	};

	const filterMenuOpen = Boolean(anchorEl);

	return (
		<>
			<Grid container spacing={1}>
				<Grid item>
					<Chip
						label={findFilterLabel('collection')}
						color='primary'
						variant={marketplaceFilters.collection ? 'filled' : 'outlined'}
						onClick={e => handleOnFilterClick(e, 'collection')}
						onDelete={() => handleOnFilterDelete('collection')}
					/>
				</Grid>
				<Grid item>
					<Chip
						label={findFilterLabel('sort')}
						color='primary'
						variant={marketplaceFilters.sort ? 'filled' : 'outlined'}
						onClick={e => handleOnFilterClick(e, 'sort')}
						onDelete={() => handleOnFilterDelete('sort')}
					/>
				</Grid>
				<Grid item>
					<Chip
						label='Reset filters'
						color='secondary'
						variant='outlined'
						onClick={handleOnResetFilter}
						onDelete={handleOnResetFilter}
					/>
				</Grid>
			</Grid>
			{filterMenuType !== null ? (
				<Menu
					id={`${filterMenuType}-menu`}
					anchorEl={anchorEl}
					open={filterMenuOpen}
					onClose={handleOnFiltersClose}
					MenuListProps={{
						'aria-labelledby': `${filterMenuType}-button`,
						role: 'listbox'
					}}
				>
					{menu[filterMenuType].map(option => {
						const isSelected = marketplaceFilters[filterMenuType] === option.key;

						return (
							<MenuItem
								key={option.key}
								selected={isSelected}
								onClick={() => handleMenuItemClick(option.key)}
							>
								<ListItemIcon>
									{isSelected ? <CheckIcon color='primary' fontSize='small' /> : null}
								</ListItemIcon>
								{option.name}
							</MenuItem>
						);
					})}
				</Menu>
			) : null}
		</>
	);
};
